'use client';

import { createContext, type ReactNode, useContext, useEffect, useMemo, useRef } from 'react';
import { useRouter } from '@/i18n/navigation';
import { useFormatter, useTranslations } from 'next-intl';
import { useAuth, type AuthUser } from '@/contexts/AuthContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useToast } from '@/contexts/ToastContext';
import { useDashboardUtilities } from '@/hooks/dashboard/useDashboardUtilities';
import { useDashboardBilling, type BillingSummary } from '@/hooks/dashboard/useDashboardBilling';
import { useDashboardProjects } from '@/hooks/dashboard/useDashboardProjects';
import { useDashboardFolderActions } from '@/hooks/dashboard/useDashboardFolderActions';
import type { Project as ProjectSummary } from '@/types/project';
import type { Workspace } from '@/types/shared/org';
import type { ProjectFolder, ProjectFolderVisibility } from '@/components/home/theme-editor';

// ── Types ────────────────────────────────────────────────────────────────────
// `BillingSummary` is defined in `useDashboardBilling` and imported back here for
// the context value type (it was module-private before the Phase 1.6 extraction).

type ProjectContextMenuState = {
  project: ProjectSummary;
  x: number;
  y: number;
  view: 'main' | 'transfer' | 'folder';
};

// ── Modal Reducer (Sub-decision D, H1c-ADR-001) ──────────────────────────────
// The modal-state sprawl across 12 useState slots used to require:
//   • 4 close `useCallback`s captured by the workspace-change effect
//   • a physical-order workaround silenced by `eslint-disable react-hooks/exhaustive-deps`
//   • mutating-flavoured paired writes that violated `react-hooks/preserve-manual-memoization`
//
// The reducer + types live in a dedicated module so the pure transition table
// can be imported from tests without dragging in the React / next-intl /
// fetchWithAuth side-imports of this provider module. We re-export here so the
// public API surface is unchanged.

export { modalReducer, INITIAL_MODAL_STATE, type ModalState, type ModalAction } from './DashboardContext.modal-reducer';
import { useDashboardModals } from '@/hooks/dashboard/useDashboardModals';

// ── Context Interface ─────────────────────────────────────────────────────────

interface DashboardContextValue {
  // ── Data ──
  projects: ProjectSummary[];
  projectFolders: ProjectFolder[];
  isInitialLoad: boolean;
  projectsLoadError: string | null;
  reload: () => Promise<void>;

  // ── Auth/Workspace (re-exported for convenience) ──
  user: AuthUser | null;
  activeWorkspace: Workspace | null;
  isAuthenticated: boolean;
  authRequired: boolean;

  // ── Billing ──
  billingSummary: BillingSummary | null;
  billingLoading: boolean;
  isFree: boolean;

  // ── Project Actions ──
  toggleProjectStar: (project: ProjectSummary) => Promise<void>;
  openProjectChat: (project: ProjectSummary) => void;
  applyUpdatedProject: (project: ProjectSummary) => void;

  // ── Project Context Menu ──
  projectContextMenu: ProjectContextMenuState | null;
  openProjectContextMenu: (event: React.MouseEvent<HTMLElement>, project: ProjectSummary) => void;
  closeProjectContextMenu: () => void;

  // ── Delete/Rename Modals ──
  deleteModal: { isOpen: boolean; project: ProjectSummary | null };
  renameModal: { isOpen: boolean; project: ProjectSummary | null; value: string };
  openDeleteModal: (project: ProjectSummary) => void;
  closeDeleteModal: () => void;
  openRenameModal: (project: ProjectSummary) => void;
  closeRenameModal: () => void;
  setRenameModalValue: (value: string) => void;

  // ── Folder Management State ──
  createFolderModalOpen: boolean;
  folderEditorMode: 'create' | 'edit';
  folderEditorTarget: ProjectFolder | null;
  createFolderName: string;
  createFolderVisibility: ProjectFolderVisibility;
  folderDeleteTarget: ProjectFolder | null;
  folderAddProjectsTarget: ProjectFolder | null;
  createSubfolderTarget: ProjectFolder | null;
  moveFolderTarget: ProjectFolder | null;
  setCreateFolderName: (name: string) => void;
  setCreateFolderVisibility: (visibility: ProjectFolderVisibility) => void;

  // ── Folder Management Actions ──
  openCreateFolderModal: () => void;
  closeCreateFolderModal: () => void;
  openEditFolderModal: (folder: ProjectFolder) => void;
  openDeleteFolderModal: (folder: ProjectFolder) => void;
  closeDeleteFolderModal: () => void;
  openAddProjectsModal: (folder: ProjectFolder) => void;
  closeAddProjectsModal: () => void;
  openCreateSubfolderModal: (folder: ProjectFolder) => void;
  closeCreateSubfolderModal: () => void;
  openMoveFolderModal: (folder: ProjectFolder) => void;
  closeMoveFolderModal: () => void;
  handleMoveProjectToFolder: (project: ProjectSummary, folderId: string | null) => Promise<void>;
  applyCreatedFolder: (folder: ProjectFolder) => void;
  applyUpdatedFolder: (folder: ProjectFolder) => void;
  applyDeletedFolder: (folderId: string) => void;

  // ── Utilities ──
  formatTime: (dateString: string | null) => string;
  getProjectThumbnailSrc: (project: ProjectSummary) => string | null;
  getProjectActivityDate: (project: ProjectSummary) => string | null;
  profileInitial: string;
  profileDisplayName: string;
}

// ── Context ───────────────────────────────────────────────────────────────────

const DashboardContext = createContext<DashboardContextValue | undefined>(undefined);

// ── Provider ──────────────────────────────────────────────────────────────────

export function DashboardProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const { user, isAuthenticated, isLoading: authLoading, authRequired } = useAuth();
  const { activeWorkspace } = useWorkspace();

  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  // ── Toast (from ToastContext) ────────────────────────────────────────────────

  const { showToast } = useToast();

  // ── i18n ────────────────────────────────────────────────────────────────────

  const tToast = useTranslations('home.toast');
  const tTime = useTranslations('common.time');
  const formatter = useFormatter();

  // ── Modal State (Sub-decision D, extracted to useDashboardModals) ───────────
  // The single `modalReducer` slot, all dispatch wrappers, the derived getters,
  // and the workspace-change close-all effect live in the hook. The provider
  // re-exposes its values verbatim through the context `value` below.

  const {
    projectContextMenu,
    openProjectContextMenu,
    closeProjectContextMenu,
    deleteModal,
    renameModal,
    openDeleteModal,
    closeDeleteModal,
    openRenameModal,
    closeRenameModal,
    setRenameModalValue,
    createFolderModalOpen,
    folderEditorMode,
    folderEditorTarget,
    createFolderName,
    createFolderVisibility,
    folderDeleteTarget,
    folderAddProjectsTarget,
    createSubfolderTarget,
    moveFolderTarget,
    setCreateFolderName,
    setCreateFolderVisibility,
    openCreateFolderModal,
    closeCreateFolderModal,
    openEditFolderModal,
    openDeleteFolderModal,
    closeDeleteFolderModal,
    openAddProjectsModal,
    closeAddProjectsModal,
    openCreateSubfolderModal,
    closeCreateSubfolderModal,
    openMoveFolderModal,
    closeMoveFolderModal,
  } = useDashboardModals({ activeWorkspaceId: activeWorkspace?.id });

  // ── Projects + Folders (extracted to useDashboardProjects, Phase 1.6) ────────
  // The normalize/sort helpers, load() with its full fan-out + per-result error
  // matrix, the microtask-deferred load-trigger effect, and the project actions
  // live in the hook. `load` is returned under its own name so the context value
  // (`reload: load`) and handleMoveProjectToFolder stay unchanged; setProjectFolders
  // + normalizeProjectPayload are surfaced for the (still-inline) folder actions.
  // isMountedRef stays provider-owned and is injected.

  const {
    projects,
    projectFolders,
    isInitialLoad,
    projectsLoadError,
    load,
    applyUpdatedProject,
    toggleProjectStar,
    openProjectChat,
    setProjectFolders,
    normalizeProjectPayload,
  } = useDashboardProjects({ user, activeWorkspace, authLoading, router, showToast, tToast, isMountedRef });

  // ── Billing (extracted to useDashboardBilling, Phase 1.6) ────────────────────
  // The /api/billing/me effect — with its cancellation control-flow, the two
  // microtask-deferred setState points, and the per-field clamp table — plus the
  // `isFree` derivation live in the hook. The `user?.id` / `activeWorkspace?.id`
  // re-trigger deps are injected as scalars; the returned values flow into the
  // context `value` below under the same field names.

  const { billingSummary, billingLoading, isFree } = useDashboardBilling({
    authLoading,
    isAuthenticated,
    userId: user?.id,
    activeWorkspaceId: activeWorkspace?.id,
  });

  // ── Utilities ────────────────────────────────────────────────────────────────
  // Pure presentation helpers extracted to a dedicated hook (Phase 1.6). The
  // closed-over `user`, `tTime`, and `formatter` are injected as params; the
  // helper bodies are unchanged, and the values flow into the context value
  // object below under the same field names.

  const { formatTime, getProjectThumbnailSrc, getProjectActivityDate, profileInitial, profileDisplayName } =
    useDashboardUtilities({ user, tTime, formatter });

  // ── Folder Actions (extracted to useDashboardFolderActions, Phase 1.6) ───────
  // handleMoveProjectToFolder + the applyCreated/Updated/DeletedFolder helpers
  // live in the hook; the projects-hook collaborators (applyUpdatedProject,
  // reload=load, normalizeProjectPayload, setProjectFolders) and the modal close
  // are injected. The returned values flow into the context value unchanged.

  const { handleMoveProjectToFolder, applyCreatedFolder, applyUpdatedFolder, applyDeletedFolder } =
    useDashboardFolderActions({
      applyUpdatedProject,
      reload: load,
      normalizeProjectPayload,
      closeProjectContextMenu,
      setProjectFolders,
      showToast,
      tToast,
    });

  // ── Context Value ─────────────────────────────────────────────────────────────

  // TODO: This context still bundles many values. Consider further splitting into
  // sub-contexts: ModalContext, FolderContext, ProjectsContext (ToastContext already extracted).
  const value = useMemo<DashboardContextValue>(
    () => ({
      // Data
      projects,
      projectFolders,
      isInitialLoad,
      projectsLoadError,
      reload: load,
      // Auth / Workspace
      user,
      activeWorkspace,
      isAuthenticated,
      authRequired,
      // Billing
      billingSummary,
      billingLoading,
      isFree,
      // Project Actions
      toggleProjectStar,
      openProjectChat,
      applyUpdatedProject,
      // Context Menu
      projectContextMenu,
      openProjectContextMenu,
      closeProjectContextMenu,
      // Delete / Rename Modals
      deleteModal,
      renameModal,
      openDeleteModal,
      closeDeleteModal,
      openRenameModal,
      closeRenameModal,
      setRenameModalValue,
      // Folder Modal State
      createFolderModalOpen,
      folderEditorMode,
      folderEditorTarget,
      createFolderName,
      createFolderVisibility,
      folderDeleteTarget,
      folderAddProjectsTarget,
      createSubfolderTarget,
      moveFolderTarget,
      setCreateFolderName,
      setCreateFolderVisibility,
      // Folder Actions
      openCreateFolderModal,
      closeCreateFolderModal,
      openEditFolderModal,
      openDeleteFolderModal,
      closeDeleteFolderModal,
      openAddProjectsModal,
      closeAddProjectsModal,
      openCreateSubfolderModal,
      closeCreateSubfolderModal,
      openMoveFolderModal,
      closeMoveFolderModal,
      handleMoveProjectToFolder,
      applyCreatedFolder,
      applyUpdatedFolder,
      applyDeletedFolder,
      // Utilities
      formatTime,
      getProjectThumbnailSrc,
      getProjectActivityDate,
      profileInitial,
      profileDisplayName,
    }),
    [
      projects,
      projectFolders,
      isInitialLoad,
      projectsLoadError,
      load,
      user,
      activeWorkspace,
      isAuthenticated,
      authRequired,
      billingSummary,
      billingLoading,
      isFree,
      toggleProjectStar,
      openProjectChat,
      applyUpdatedProject,
      projectContextMenu,
      openProjectContextMenu,
      closeProjectContextMenu,
      deleteModal,
      renameModal,
      openDeleteModal,
      closeDeleteModal,
      openRenameModal,
      closeRenameModal,
      setRenameModalValue,
      createFolderModalOpen,
      folderEditorMode,
      folderEditorTarget,
      createFolderName,
      createFolderVisibility,
      folderDeleteTarget,
      folderAddProjectsTarget,
      createSubfolderTarget,
      moveFolderTarget,
      setCreateFolderName,
      setCreateFolderVisibility,
      openCreateFolderModal,
      closeCreateFolderModal,
      openEditFolderModal,
      openDeleteFolderModal,
      closeDeleteFolderModal,
      openAddProjectsModal,
      closeAddProjectsModal,
      openCreateSubfolderModal,
      closeCreateSubfolderModal,
      openMoveFolderModal,
      closeMoveFolderModal,
      handleMoveProjectToFolder,
      applyCreatedFolder,
      applyUpdatedFolder,
      applyDeletedFolder,
      formatTime,
      getProjectThumbnailSrc,
      getProjectActivityDate,
      profileInitial,
      profileDisplayName,
    ],
  );

  return <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>;
}

// ── Hook ───────────────────────────────────────────────────────────────────────

export function useDashboard(): DashboardContextValue {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within DashboardProvider');
  }
  return context;
}
